import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

/**
 * Typowany dostęp do tabeli `public.subscriptions`. Tabela jest dodana migracją
 * 20260506120000_create_subscriptions.sql, ale `src/integrations/supabase/types.ts`
 * jest auto-generowany — do czasu jego regeneracji (`supabase gen types …`) używamy
 * lokalnie zadeklarowanego schematu, żeby kod pozostał strict-bez-`any`.
 */

export type SubscriptionStatus =
  | "active"
  | "canceled"
  | "past_due"
  | "trialing"
  | "incomplete"
  | "incomplete_expired"
  | "unpaid";

export type SubscriptionPlan = "founders" | "normal";

export interface SubscriptionRow {
  id: string;
  user_id: string;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  status: SubscriptionStatus;
  plan: SubscriptionPlan;
  current_period_end: string | null;
  founders: boolean;
  created_at: string;
  updated_at: string;
}

interface SubscriptionsSchema {
  public: {
    Tables: {
      subscriptions: {
        Row: SubscriptionRow;
        Insert: Omit<SubscriptionRow, "id" | "created_at" | "updated_at"> &
          Partial<Pick<SubscriptionRow, "id" | "created_at" | "updated_at">>;
        Update: Partial<SubscriptionRow>;
        Relationships: [];
      };
    };
    Views: Record<string, never>;
    Functions: Record<string, never>;
    Enums: Record<string, never>;
    CompositeTypes: Record<string, never>;
  };
}

const ACTIVE_STATUSES: ReadonlyArray<SubscriptionStatus> = ["active", "trialing"];

function asSubscriptionsClient(): SupabaseClient<SubscriptionsSchema> {
  return supabase as unknown as SupabaseClient<SubscriptionsSchema>;
}

export async function fetchActiveSubscriptionForUser(
  userId: string,
): Promise<SubscriptionRow | null> {
  const { data, error } = await asSubscriptionsClient()
    .from("subscriptions")
    .select("*")
    .eq("user_id", userId)
    .in("status", ACTIVE_STATUSES as unknown as string[])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;
  return data;
}
